import { useCallback, useEffect, useMemo } from "react";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { useTeleconsultoriaProfile } from "../../profile/TeleconsultoriaProfileContext";
import type { TeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { isSamePractitionerReference } from "../../utils/actors";
import { loadClinicActivity, type ClinicActivity } from "../services/clinicActivityService";

const EMPTY_ACTIVITY: ClinicActivity = { inviteRows: [], bookedRows: [] };

/**
 * Convites e marcadas da PRESTADORA (visão clínica) no período do filtro.
 * `professionalReference`: filtra no cliente pelo CPF (sem nova busca);
 * vazio = todos os profissionais.
 */
export function useProviderClinicActivity(
  range: TeleconsultoriaDateRange,
  professionalReference?: string,
) {
  const { profile } = useTeleconsultoriaProfile();
  const organizationToken = profile.kind === "provider" ? profile.organizationToken : "";
  const { date, mode, startDate, endDate } = range;

  const loadTask = useAsyncTask({
    task: useCallback(
      () => loadClinicActivity({ organizationToken, date, mode, startDate, endDate }),
      [organizationToken, date, mode, startDate, endDate],
    ),
    initialData: EMPTY_ACTIVITY,
  });

  const { run } = loadTask;
  const reload = useCallback(() => {
    void run().catch((error) => console.error("useProviderClinicActivity", error));
  }, [run]);

  useEffect(() => {
    if (organizationToken) reload();
  }, [organizationToken, reload]);

  const activity = loadTask.data ?? EMPTY_ACTIVITY;
  const filtered = useMemo(() => {
    if (!professionalReference) return activity;
    return {
      inviteRows: activity.inviteRows.filter((row) =>
        isSamePractitionerReference(row.guestReference, professionalReference),
      ),
      bookedRows: activity.bookedRows.filter((row) =>
        isSamePractitionerReference(row.teleconsultorReference, professionalReference),
      ),
    };
  }, [activity, professionalReference]);

  return {
    ...filtered,
    isLoading: loadTask.loading,
    hasError: loadTask.status === "error",
    reload,
  };
}
